import React, { useEffect, useState } from 'react';
import { Target, Plus, ThumbsUp, Trash2, Save, Eye } from 'lucide-react';
import Modal from '../../common/Modal';
import FormControl from '../../common/FormControl';
import Button from '../../common/Button';
import Toast, { ToastMessage } from '../../common/Toast';
import { confirmDelete } from '../../common/confirm';
import { capaImpactsService, CapaImpact, ImpactSeverity } from '../../../services/capaImpactsService';
import { capaImpactCategoriesService, ImpactCategory } from '../../../services/capaImpactCategoriesService';
import extractApiError from '../../../utils/apiError';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../../validation/ValidatedFields';
import ImpactDetailModal from './ImpactDetailModal';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';

const SEVERITIES: ImpactSeverity[] = ['Low', 'Medium', 'High', 'Critical'];

const severityVariant = (s: ImpactSeverity): string => {
    switch (s) {
        case 'Critical': return 'danger';
        case 'High':     return 'warning';
        case 'Medium':   return 'info';
        default:         return 'success'; // Low
    }
};

interface Props {
    investigationId: number;
    impacts: CapaImpact[];
    selectedImpactId: number | null;
    onSelect: (id: number | null) => void;
    onChanged: () => void;
    isLocked?: boolean;
}

const ImpactColumn: React.FC<Props> = ({ investigationId, impacts, selectedImpactId, onSelect, onChanged, isLocked }) => {
    const [categories, setCategories] = useState<ImpactCategory[]>([]);
    const [showAdd, setShowAdd] = useState(false);
    const [detailImpact, setDetailImpact] = useState<CapaImpact | null>(null);
    const [form, setForm] = useState<{
        impact_category_id: number | null;
        impact_description: string;
        impact_severity: ImpactSeverity;
        impact_amount: string;
    }>({ impact_category_id: null, impact_description: '', impact_severity: 'Medium', impact_amount: '' });
    const [submitting, setSubmitting] = useState(false);
    const [votingId, setVotingId] = useState<number | null>(null);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const fieldVh = useFieldValidation();
    const { formatCurrency } = useEntitySettings();

    useEffect(() => {
        capaImpactCategoriesService.dropdown().then(setCategories).catch(() => {});
    }, []);

    useEffect(() => {
        if (!showAdd) return;
        fieldVh.reset();
    }, [showAdd, fieldVh]);

    const openAdd = () => {
        setForm({ impact_category_id: null, impact_description: '', impact_severity: 'Medium', impact_amount: '' });
        setShowAdd(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required fields
        if (!form.impact_category_id) {
            fieldVh.setError('impact_category_id', 'Category is required');
            return;
        }
        fieldVh.clearError('impact_category_id');

        if (!form.impact_severity) {
            fieldVh.setError('impact_severity', 'Severity is required');
            return;
        }
        fieldVh.clearError('impact_severity');

        // Validate code 110 field
        const valid = fieldVh.validateAll([
            { name: 'impact_description', code: 110, value: form.impact_description },
        ]);
        if (!valid) return;

        try {
            setSubmitting(true);
            await capaImpactsService.create(investigationId, {
                impact_category_id: form.impact_category_id,
                impact_description: form.impact_description || undefined,
                impact_amount: form.impact_amount ? Number(form.impact_amount) : null,
                impact_severity: form.impact_severity,
            });
            setToast({ type: 'success', message: 'Impact added.' });
            setShowAdd(false);
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to add impact') });
        } finally { setSubmitting(false); }
    };

    const handleVote = async (impactId: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (isLocked || votingId !== null) return; // guard against rapid double-clicks
        try {
            setVotingId(impactId);
            await capaImpactsService.toggleVote(impactId);
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to vote') });
        } finally {
            setVotingId(null);
        }
    };

    const handleDelete = async (impact: CapaImpact, e: React.MouseEvent) => {
        e.stopPropagation();
        if (isLocked) return;
        const ok = await confirmDelete({ text: `Delete this impact?` });
        if (!ok) return;
        try {
            await capaImpactsService.remove(investigationId, impact.id);
            setToast({ type: 'delete', message: 'Deleted.' });
            if (selectedImpactId === impact.id) onSelect(null);
            onChanged();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to delete') });
        }
    };

    const categoryOptions = categories.map(c => ({ value: c.id, label: c.impact_category_name }));

    return (
        <div className="capa-investigation-col">
            <div className="capa-investigation-col-header">
                <Target size={14} />
                Impact Assessment
                <span className="capa-investigation-col-header-spacer" />
                {!isLocked && (
                    <button
                        type="button"
                        onClick={openAdd}
                        className="btn-icon-only"
                        data-tooltip="Add impact"
                        data-tooltip-pos="right"
                        data-testid="BTN_impact_add"
                    >
                        <Plus size={16} />
                    </button>
                )}
            </div>
            <div className="capa-investigation-col-body">
                {impacts.length === 0 && (
                    <div className="capa-investigation-col-empty">No impacts yet. Click + to add one.</div>
                )}
                {impacts.map(imp => (
                    <div
                        key={imp.id}
                        className={`capa-impact-card ${selectedImpactId === imp.id ? 'capa-impact-card--selected' : ''}`}
                        onClick={() => onSelect(imp.id)}
                        data-testid={`BTN_impact_card_${imp.id}_select`}
                    >
                        <div className="capa-impact-card-head">
                            <span className={`status-badge xs ${severityVariant(imp.impact_severity)}`}>
                                {imp.impact_severity}
                            </span>
                            <span className="table-list-cell-sub" style={{ fontSize: '0.7rem' }}>
                                {imp.impact_category_name}
                            </span>
                        </div>
                        {imp.impact_description && (
                            <div className="capa-impact-card-desc">{imp.impact_description}</div>
                        )}
                        <div className="capa-impact-card-foot">
                            <button
                                type="button"
                                className={`capa-impact-vote ${imp.user_voted ? 'capa-impact-vote--active' : ''}`}
                                onClick={(e) => handleVote(imp.id, e)}
                                disabled={isLocked || votingId !== null}
                                title={isLocked ? 'Read-only' : (imp.user_voted ? 'Remove your vote' : 'Vote up')}
                                data-testid={`BTN_impact_row_${imp.id}_vote`}
                            >
                                <ThumbsUp size={11} />
                                {imp.vote_total || 0}
                            </button>
                            {imp.impact_amount != null && (
                                <span>{formatCurrency(imp.impact_amount)}</span>
                            )}
                            {!isLocked && (
                                <button
                                    type="button"
                                    onClick={(e) => handleDelete(imp, e)}
                                    className="btn-danger-outline-none"
                                    title="Delete"
                                    data-testid={`BTN_impact_row_${imp.id}_delete`}
                                >
                                    <Trash2 size={11} />
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn-primary-outline-none"
                                onClick={(e) => { e.stopPropagation(); setDetailImpact(imp); }}
                                title="View / edit impact"
                                data-testid={`BTN_impact_${imp.id}_view`}
                            >
                                <Eye size={12} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} size="md">
                <Modal.Header>Create Impact</Modal.Header>
                <Modal.Body>
                    <form id="add-impact-form" onSubmit={handleSubmit} className="grid grid-cols-2 gap-x-6 gap-y-4" data-testid="MOD_impact_add_form">
                        <FormControl.SearchSelect
                            label="Category"
                            required
                            options={categoryOptions}
                            value={form.impact_category_id}
                            onChange={v => setForm(f => ({ ...f, impact_category_id: v ? Number(v) : null }))}
                            clearable
                            data-testid="SEL_impact_category"
                        />
                        <FormControl.SearchSelect
                            label="Severity"
                            required
                            options={SEVERITIES.map(s => ({ value: s, label: s }))}
                            value={form.impact_severity}
                            onChange={v => setForm(f => ({ ...f, impact_severity: (v as ImpactSeverity) ?? 'Medium' }))}
                            data-testid="SEL_impact_severity"
                        />
                        <div className="col-span-2">
                            <ValidatedTextarea
                                vh={fieldVh}
                                name="impact_description"
                                code={110}
                                label="Description"
                                value={form.impact_description}
                                onChange={e => setForm(f => ({ ...f, impact_description: e.target.value }))}
                                placeholder="Describe how this issue manifests as an impact"
                                data-testid="INP_impact_description"
                            />
                        </div>
                        <FormControl.Input
                            label="Financial Amount (optional)"
                            type="number"
                            value={form.impact_amount}
                            onChange={e => setForm(f => ({ ...f, impact_amount: e.target.value }))}
                            placeholder="0"
                            data-testid="INP_impact_amount"
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" form="add-impact-form" variant="fill" icon={Save} disabled={submitting} data-testid="BTN_impact_submit">
                        {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ImpactDetailModal
                isOpen={!!detailImpact}
                investigationId={investigationId}
                impact={detailImpact}
                frozen={!!isLocked}
                categories={categoryOptions}
                onClose={() => setDetailImpact(null)}
                onChanged={onChanged}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default ImpactColumn;
